<script>
    import { select } from 'd3-selection';
    import { scaleTime, scaleLinear } from 'd3-scale';
    import { max } from 'd3-array';
    import { axisBottom, axisLeft } from 'd3-axis';
    import { timeYears } from 'd3-time';
    import debounce from 'lodash.debounce';
    import { onMount } from 'svelte';

    export let monitoringResults, tests, analyte;

    const minDate = new Date(2011, 0);
    const maxDate = new Date(2023, 12);
    const years = [minDate, maxDate];
    let outerWidth = 220;

    /** @type {HTMLDivElement} */
    let chart_container;
    /** @type {HTMLDivElement} */
    let no_results;

    onMount(() => {
        setDimensions();
        drawBars(); 
    });

    const onResize = debounce(function onResize() {
        setDimensions();
        drawBars(); 
    }, 300);

    $: drawBars(monitoringResults);

    function setDimensions() {
        outerWidth = chart_container.offsetWidth;
    }

    function drawBars() {
        var container = select(chart_container);
        container.select('svg').remove();

        var no_results_container = select(no_results);

        var pwsid = monitoringResults[0].pwsid;

        var recent = monitoringResults.filter(x => x[analyte] != '').slice(-1)[0];
        var analyte_name = analyte.replace('tcp', '1,2,3-TCP');
        var analyte_name = analyte_name.replace('_', ' ');
        var unit_label = analyte.includes("arsenic") ? "parts per billion" : analyte.includes("nitrate") ? "parts per million" : "parts per trillion";

        // Add in the maximum contaminant level and the public health goal
        var recent_value = recent == undefined ? 
        'There are no results for ' + analyte_name + '.' : recent[analyte] == '0.0' ? 
        'The most recent levels for ' + analyte_name + ' are from ' + recent.year.getFullYear() + ' and were below the reporting threshold.' :
        'The most recent levels for ' + analyte_name + ' are from ' + recent.year.getFullYear() + ' and were ' + Math.round(recent[analyte]) + ' ' +  unit_label + '.';

        // Get the max value from the full dataset
        let maxValue = pwsid == "CA1510052" ? 91.5 : max(monitoringResults, function (d) { return +d[analyte]; });

        let margin = maxValue > 100 ? {top: 10, right: 0, bottom: 20, left: 30} : {top: 10, right: 20, bottom: 20, left: 16};
        let width = outerWidth - margin.left - margin.right;
        let height = 200 - margin.top - margin.bottom;

        if (monitoringResults.length > 0) {
            if (tests > 0) {

            container.attr('aria-label', recent_value);
            no_results_container.attr('aria-hidden', true);

            // Add Y axis
            const y = scaleLinear()
            .domain(maxValue < 20 ? [0, 20] : [0, maxValue])
            .range([height, 0]);

            // Add X axis
            const x = scaleTime().domain(years).range([0, width]);
            const allYears = timeYears(x.domain()[0], x.domain()[1], 1);

            // Append the svg object to the body of the page
            const svg = container
            .append('svg')
            .attr('width', width + margin.left + margin.right)
            .attr('height', height + margin.top + margin.bottom)
            .append('g')
            .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

            svg
            .selectAll('.no-bars')
            .data(allYears)
            .enter()
            .append('rect')
            .attr('class', 'no-bars')
            .attr('fill', "url(#hatch)")
            .attr('x', function (d) {
                return x(d);
            })
            .attr('y', maxValue < 20 ? y(20) : y(maxValue))
            .attr('height', maxValue < 20 ? y(0) - y(20) : y(0) - y(maxValue))
            .attr('width', width / allYears.length); // divide the width by the number of years

            svg
            .selectAll('.bar-background')
            .data(monitoringResults)
            .enter()
            .append('rect')
            .attr('class', 'bar-background')
            .attr('fill', function(d) { return d[analyte] == '' ? 'transparent' : '#fff' })
            .attr('x', function (d) {
                return x(d.year);
            })
            .attr('y', maxValue < 20 ? y(20) : y(maxValue))
            .attr('height', maxValue < 20 ? y(0) - y(20) : y(0) - y(maxValue))
            .attr('width', width / allYears.length); // divide the width by the number of years

            svg
            .selectAll('.bars')
            .data(monitoringResults)
            .enter()
            .append('rect')
            .attr('class', 'bars')
            .attr('fill', '#565656')
            .attr('x', function (d) {
                return x(d.year);
            })
            .attr('y', function (d) {
                return y(d[analyte]);
            })
            .attr('height', function (d) {
                return y(0) - y(d[analyte]);
            })
            .attr('width', width / allYears.length); // divide the width by the number of years

            svg
            .selectAll('.bar-overlay')
            .data(monitoringResults)
            .enter()
            .append('rect')
            .attr('class', 'bar-overlay')
            .attr('fill', function(d) { return d[analyte] == '' ? 'transparent' : '#fff' })
            .attr('opacity', 0.6)
            .attr('x', function (d) {
                return x(d.year);
            })
            .attr('width', width / allYears.length)
            .attr('y', analyte.includes('tcp') ? y(5) : y(10))
            .attr('height', analyte.includes('tcp') ? y(0) - y(5) : y(0) - y(10));

            svg
            .append('g')
            .attr('class', 'axis y')
            .call(axisLeft(y).ticks(6).tickSize(-width));

            svg
            .append('g')
            .attr('transform', 'translate(0,' + height + ')')
            .attr('class', 'axis x')
            .call(axisBottom(x).tickValues(outerWidth <= 180 ? [new Date(2015, 0), new Date(2020, 0)] : [new Date(2014, 0), new Date(2018, 0), new Date(2022, 0)]));

            if(analyte.includes("arsenic")) {

                // Add the PHG line
                svg
                .append('line')
                .attr('x1', x(years[0]))
                .attr('x2', x(years[1]))
                .attr('y1', y(0.004))
                .attr('y2', y(0.004))
                .attr('class', 'phg-line');
                // Add the MCL line
                svg
                .append('line')
                .attr('x1', x(years[0]))
                .attr('x2', x(years[1]))
                .attr('y1', y(10))
                .attr('y2', y(10))
                .attr('class', 'mcl-line');
                // Add the MCL label

            } else if(analyte.includes("nitrate")) {

                // Add the PHG line
                svg
                .append('line')
                .attr('x1', x(years[0]))
                .attr('x2', x(years[1]))
                .attr('y1', y(10))
                .attr('y2', y(10))
                .attr('class', 'phg-line');
                // Add the MCL line
                svg
                .append('line')
                .attr('x1', x(years[0]))
                .attr('x2', x(years[1]))
                .attr('y1', y(10))
                .attr('y2', y(10))
                .attr('class', 'mcl-line');

            } else {
                // Add the PHG line
                svg
                .append('line')
                .attr('x1', x(years[0]))
                .attr('x2', x(years[1]))
                .attr('y1', y(0.07))
                .attr('y2', y(0.07))
                .attr('class', 'phg-line');
                // Add the MCL line
                svg
                .append('line')
                .attr('x1', x(years[0]))
                .attr('x2', x(years[1]))
                .attr('y1', y(5))
                .attr('y2', y(5))
                .attr('class', 'mcl-line');
            }

        }
    }
    }

</script>

<svelte:window on:resize|passive={onResize} />

<div class="chart monitoring" bind:this={chart_container}>
    <div class="no-results" bind:this={no_results}>There are no results</div>
</div>